import { bcApi } from "../services/apiClients";
import { formatAxiosError } from "./common";
import { avisoDelFallo, ORIGEN_BC, origenDelFallo } from "./avisoDelFallo";
import { comportamientoDelPais } from "../config/integracionPorPais";
import { anotarAvisoDeTransporte, anotarTransporte } from "../services/syncLog";
import { getActiveEnvironment, guardarCompanyIdActivo } from "../services/environmentService";
import { encodeBcCompanyName } from "./stringNormalization";
import enviroment from "../config/env";

/**
 * Le pone nombre al error de una lectura antes de volver a lanzarlo.
 *
 * Un 404 de OData llega con el cuerpo vacío, así que `formatAxiosError` cae a
 * `error.message` y la pantalla muestra «Request failed with status code 404»
 * a secas: ni qué entidad falló ni por qué. Traer Históricos consulta dieciséis,
 * y con ese texto hay que ir preguntándoselas a Business Central una por una
 * para dar con la que falta -- que es exactamente lo que hubo que hacer el
 * 27/08/2026, cuando un despliegue dejó `PostedSalesInvoice` y
 * `PostedSalesCreditMemo` sin publicar.
 *
 * El 404 se explica, además de nombrarse: sobre una entidad de OData no
 * significa «no hay datos» --eso sería una lista vacía-- sino que el servicio
 * web no está registrado con ese nombre.
 *
 * Se modifica el error en el sitio en vez de crear otro: los que lo reciben
 * miran `error.response.status` para decidir si reintentan, y envolverlo se lo
 * escondería.
 */
const nombrarErrorDeLectura = (error, endpoint) => {
  // El endpoint trae la consulta entera pegada; para el aviso basta la entidad.
  const entidad = String(endpoint ?? "").split("?")[0] || "Business Central";
  const estado = error?.response?.status;
  const detalle = error?.response?.data?.error?.message || error?.response?.data?.message;

  if (estado === 404 && !detalle) {
    error.message = `Business Central no publica «${entidad}»: el servicio web no está registrado con ese nombre (404).`;

    // Y no se reintenta.
    //
    // La sincronización de maestros envuelve cada paso en `executeWithRetry`:
    // cinco intentos con quince segundos de espera y, al agotarlos, un modal de
    // «Error Crítico» que **recarga la aplicación sola** a los treinta segundos.
    // Minuto y medio, y al volver a arrancar pasa lo mismo, porque un servicio
    // que no está publicado no aparece por recargar el navegador.
    //
    // Con esta marca el fallo sale a la primera y con su nombre, por el mismo
    // camino que usa la precondición de caja abierta. El 27/08/2026, cuando un
    // despliegue dejó dos entidades en 404, esto habría ahorrado el bucle en
    // cada caja -- y ahí sólo cayeron dos entidades de documentos; si llega a
    // caer un maestro, cada terminal se reinicia solo sin decir por qué.
    error.nonRetryable = true;
  } else if (detalle) {
    error.message = `No se pudo leer «${entidad}» de Business Central: ${detalle}`;
  } else {
    error.message = `No se pudo leer «${entidad}» de Business Central: ${
      error?.message || "error desconocido"
    }`;
  }

  return error;
};

export const getCurrentEnvironment = async () => {
  try {
    const activeEnv = await getActiveEnvironment();
    return { ...activeEnv, bcBaseUrl: enviroment.bcBaseUrl } || enviroment;
  } catch (error) {
    console.warn("No se pudo obtener configuración activa, usando predeterminada:", error);
    return enviroment;
  }
};

/**
 * El id de empresa que piden las API REST de Business Central.
 *
 * Hasta ahora salía del `.env` mientras que el nombre de empresa salía de la
 * base local, y cuando los dos no coincidían —que es lo que pasaba— la lectura
 * por OData funcionaba y la de las API daba 404 «company ID does not exist».
 * Eran dos fuentes para el mismo dato.
 *
 * Ahora manda la base local. El `.env` es sólo la semilla de una instalación
 * nueva; si el id falta —porque la empresa se cambió desde Configuración, o
 * porque la instalación es anterior a esto— se resuelve **contra Business
 * Central, por el nombre de empresa que ya usa OData**, y se guarda. Así el
 * nombre y el id salen siempre del mismo sitio y no pueden divergir.
 */
const normalizarNombreDeEmpresa = (valor) =>
  String(valor ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");

// Una sola resolución a la vez: la primera sincronización dispara varias
// llamadas en paralelo y todas necesitan el mismo id.
let resolucionEnCurso = null;

export const resolverCompanyId = async (env) => {
  if (env?.companyId) return env.companyId;

  if (!resolucionEnCurso) {
    resolucionEnCurso = buscarCompanyIdPorNombre(env).finally(() => {
      resolucionEnCurso = null;
    });
  }

  return resolucionEnCurso;
};

const buscarCompanyIdPorNombre = async (env) => {
  const buscado = normalizarNombreDeEmpresa(env?.company);

  if (!buscado) {
    throw new Error(
      "La configuración activa no tiene empresa, así que no se puede resolver su id en Business Central."
    );
  }

  const response = await bcApi.get(`${env.bcBaseUrl}/${env.env}/api/v2.0/companies`);
  const companies = response?.data?.value || [];
  const encontrada = companies.find(
    (company) => normalizarNombreDeEmpresa(company?.name) === buscado
  );

  if (!encontrada?.id) {
    // El mensaje nombra lo que hay: sin eso, el 404 de la API no dice nada y
    // hay que ir a mirar el entorno a mano.
    const disponibles = companies.map((company) => company?.name).join(", ") || "ninguna";
    throw new Error(
      `El entorno ${env.env} no tiene una empresa llamada "${env.company}". Publica: ${disponibles}.`
    );
  }

  anotarTransporte(
    `empresa "${encontrada.name}" resuelta a ${encontrada.id} · se guarda en la configuración`
  );
  await guardarCompanyIdActivo(encontrada.id);

  return encontrada.id;
};

// No es la forma correcta de manejarlo, en un mundo ideal si BC rompe devuelve un código distinto a 200
// Si falla Business Central a veces devuelve 200 y el flujo sigue normalmente! No permitiendome manejar el error como se debe
// Esta función es el último bastión para chequear si da error y permitirnos manejarlo
export const hasError = (value) => {
  if (typeof value === "string" && value.toLowerCase().startsWith("error:")) {
    return true;
  }
  return false;
};

// Devuelve el texto del error que BC manda dentro de una respuesta 200
// ("Error: ..."), o null si el valor no es un error. Se exporta para que quien
// llama pueda mostrarle al usuario el motivo real y no un mensaje genérico.
export const getBcErrorMessage = (value) => {
  if (!hasError(value)) return null;
  return value.substring(6).trim();
};

/**
 * El motivo por el que Business Central rechazó un envío, o null si lo aceptó.
 *
 * Existe porque la forma de la respuesta **no es evidente desde afuera** y ya
 * costó una falla silenciosa. `sendToBusinessCentral` devuelve el cuerpo OData,
 * cuyo contenido vive en `value`; y cuando detecta un rechazo hace dos cosas a
 * la vez: **vacía `value`** —para que nadie lo confunda con un resultado
 * bueno— y guarda el texto en `error`. De ahí que ni `respuesta.data` (que no
 * existe) ni `hasError(respuesta.value)` (que llega vacío justo cuando hay
 * error) sirvan para detectarlo. El único campo que lo dice es `error`.
 *
 * El respaldo por `value` está por si alguna respuesta llega sin pasar por ese
 * vaciado; cuesta una línea y cubre el camino que no controlamos.
 */
export const motivoDeRechazoBC = (respuesta) => {
  if (!respuesta) return null;
  if (respuesta.error) return respuesta.error;
  return getBcErrorMessage(respuesta.value) ?? rechazoEnResultados(respuesta.value);
};

/**
 * El primer renglón rechazado de una respuesta con `Results`, o null.
 *
 * Las acciones de líneas y pagos contestaban `Ok` o `error: …`, y eso es lo único
 * que se leía. Pero sobre un documento ya registrado Business Central contesta
 * con el envoltorio de resultados --medido en Costa Rica DEV con la 1.0.4.109:
 * `{"Results":[{"Ok":false,"Accion":"documento_registrado","Mensaje":"…"}]}`--,
 * y ese `Ok:false` pasaba por aceptado. Acuerdo
 * `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H1.
 */
const rechazoEnResultados = (valor) => {
  if (typeof valor !== "string" || !valor.trim().startsWith("{")) return null;

  const respuesta = safeJsonParse(valor);
  if (!respuesta || !Array.isArray(respuesta.Results)) return null;

  const rechazado = respuesta.Results.find((renglon) => renglon?.Ok === false);
  if (!rechazado) return null;

  return rechazado.Mensaje || rechazado.Accion || "Business Central rechazó el envío";
};

/**
 * El aviso que Business Central concatena detrás de un `Ok`.
 *
 * Las acciones de líneas no devuelven `Results[]`: contestan `'Ok'` a secas o
 * `'error: ...'`. Desde el contrato de idempotencia, un `Ok` puede traer un
 * aviso pegado --por ejemplo, que el `PorIva` recibido difiere del que BC
 * aplicó, señal de un catálogo local desactualizado--. Este es su lector: sin
 * él, el aviso nacería muerto, que es justo lo que la negociación preguntó
 * («¿quién va a leer el sufijo?») antes de aceptar el mecanismo.
 *
 * Sólo un `Ok` con cola es aviso. El `Ok` pelado devuelve null, y un rechazo
 * también: eso ya lo lee `motivoDeRechazoBC`.
 */
export const avisoTrasElOk = (respuesta) => {
  const texto = typeof respuesta?.value === "string" ? respuesta.value : "";
  if (!texto.startsWith("Ok")) return null;

  const cola = texto.slice(2).trim();

  return cola || null;
};

const checkValueForErrors = (value) => {
  const errorMessage = getBcErrorMessage(value);
  if (errorMessage === null) return false;
  console.error("Error de Business Central:", errorMessage);
  return true;
};

const safeJsonParse = (text) => {
  try {
    return JSON.parse(text);
  } catch (error) {
    console.warn("Respuesta de Business Central que parecía JSON y no se pudo parsear:", text);
    return null;
  }
};

// Las acciones Importaciones_* devuelven Edm.String, o sea que todo llega como
// texto dentro de `value`. Ese texto tuvo históricamente dos formas: el No_ que
// BC asignó al registro ("E00042"), o un error con prefijo "Error: ...". BC
// puede devolver ahora un JSON con el detalle por registro:
//   {"Results":[{"No_","No_Asignado","Ok","Accion","Mensaje"}],
//    "Ok":false,"Total":3,"Correctos":2,"Errores":1}
// OJO: el "Ok" de nivel superior es el resumen del lote y puede ser false
// aunque haya registros correctos, así que nunca decide el destino de un
// registro puntual -- eso sale del "Ok" de su propia entrada en Results.
// Este helper entiende ambos formatos y devuelve una forma única, para que el
// TPV siga funcionando durante la transición sin importar cuál esté publicado.
export const parseImportResponse = (response, sentNo) => {
  // Error que BC mandó como "Error: ..." dentro de un 200; sendToBusinessCentral
  // ya lo detectó y lo guardó aparte.
  if (response?.error) {
    return { ok: false, assignedNo: null, action: "error", message: response.error };
  }

  const raw = response?.value;
  if (typeof raw !== "string" || raw.trim() === "") {
    return { ok: false, assignedNo: null, action: null, message: null };
  }

  const looksJson = raw.trim().startsWith("{");
  const parsed = looksJson ? safeJsonParse(raw) : null;

  // Venía en formato nuevo pero ilegible: no se puede dar por bueno ni tomar el
  // texto como No_, porque un No_ nunca empieza con "{".
  if (looksJson && (!parsed || !Array.isArray(parsed.Results))) {
    return {
      ok: false,
      assignedNo: null,
      action: "error",
      message: "Business Central devolvió una respuesta que no se pudo interpretar",
    };
  }

  // Formato viejo: el string ES el No_ que asignó BC.
  if (!parsed) {
    return { ok: true, assignedNo: raw, action: null, message: null };
  }

  // Formato nuevo: se busca la entrada del registro que se mandó. El TPV manda
  // un empleado por llamada, pero se cruza por No_ de todas formas por si algún
  // día se manda un lote.
  const entry =
    parsed.Results.find((r) => r?.No_ === sentNo) ||
    (parsed.Results.length === 1 ? parsed.Results[0] : null);

  if (!entry) {
    return {
      ok: false,
      assignedNo: null,
      action: "error",
      message: `Business Central no devolvió resultado para ${sentNo}`,
    };
  }

  return {
    ok: entry.Ok === true,
    assignedNo: entry.No_Asignado || null,
    action: entry.Accion || null,
    message: entry.Mensaje || null,
  };
};

/**
 * Lee la respuesta de una acción `Importaciones_*` a la que se le mandó un LOTE
 * de registros, y devuelve un resultado por cada uno más el resumen del lote.
 *
 * El cruce va por POSICIÓN, no por `No_`, y es a propósito. Business Central
 * recorre el array recibido con un `foreach` y agrega a `Results` en ese mismo
 * orden (verificado en `insertaTurnos`/`insertaColegios` del lado AL), así que
 * la posición es fiable. `No_` no lo es: un alta se manda con `No: "TEMP"`, de
 * modo que varios registros nuevos en el mismo lote vuelven todos con el mismo
 * `No_` y no habría forma de distinguirlos.
 *
 * Cuando la respuesta no se puede repartir por posición, fallan TODOS los
 * registros del lote con el mismo motivo. Es la lectura segura: dar por buenos
 * unos cuantos a ciegas dejaría la base local diciendo que se aplicaron cambios
 * que BC nunca aplicó. Ocurre en dos casos reales:
 *
 * - `RespuestaError` de BC (JSON ilegible, array ausente, sin datos), que
 *   devuelve UN solo resultado con `Total: 0` sin importar cuántos se mandaron.
 * - Cualquier otro desajuste entre lo enviado y lo devuelto.
 *
 * @param {object} response - Respuesta cruda de sendToBusinessCentral.
 * @param {number} sentCount - Cuántos registros se mandaron, en orden.
 * @returns {{ok: boolean, total: number, correctos: number, errores: number,
 *   results: Array<{ok: boolean, assignedNo: string|null, action: string|null, message: string|null}>}}
 */
export const parseImportResults = (response, sentCount) => {
  const fallarTodos = (message) => ({
    ok: false,
    total: 0,
    correctos: 0,
    errores: sentCount,
    results: Array.from({ length: sentCount }, () => ({
      ok: false,
      assignedNo: null,
      action: "error",
      message,
    })),
  });

  // Error que BC mandó como "Error: ..." dentro de un 200.
  if (response?.error) return fallarTodos(response.error);

  const raw = response?.value;
  if (typeof raw !== "string" || raw.trim() === "") {
    return fallarTodos("Business Central devolvió una respuesta vacía");
  }

  const looksJson = raw.trim().startsWith("{");
  const parsed = looksJson ? safeJsonParse(raw) : null;

  // Formato viejo: el texto ES el No_ que asignó BC. Sólo puede corresponder a
  // un registro, así que un lote con esta respuesta no se puede repartir.
  if (!looksJson) {
    if (sentCount === 1) {
      return {
        ok: true,
        total: 1,
        correctos: 1,
        errores: 0,
        results: [{ ok: true, assignedNo: raw, action: null, message: null }],
      };
    }
    return fallarTodos("Business Central respondió en el formato antiguo, que no admite lotes");
  }

  if (!parsed || !Array.isArray(parsed.Results)) {
    return fallarTodos("Business Central devolvió una respuesta que no se pudo interpretar");
  }

  if (parsed.Results.length !== sentCount) {
    return fallarTodos(
      parsed.Results[0]?.Mensaje ||
        `Business Central devolvió ${parsed.Results.length} resultados para ${sentCount} registros`
    );
  }

  const results = parsed.Results.map((entry) => ({
    ok: entry?.Ok === true,
    assignedNo: entry?.No_Asignado || null,
    action: entry?.Accion || null,
    message: entry?.Mensaje || null,
  }));

  const correctos = results.filter((r) => r.ok).length;

  // Se recalculan a partir de los resultados en vez de confiar en los contadores
  // del lote: lo que manda es lo que se va a aplicar registro por registro, y así
  // el cuadro que ve el usuario nunca contradice lo que hizo la sincronización.
  return {
    ok: correctos === results.length,
    total: results.length,
    correctos,
    errores: results.length - correctos,
    results,
  };
};

// --- Fetch helpers ---

// Tope de seguridad de páginas encadenadas por @odata.nextLink. Con el tope de
// página de BC (>= 19.950 filas, medido) esto son cientos de miles de registros:
// si se alcanza, es que algo va mal, no que la tabla sea grande.
const MAX_NEXT_LINKS = 50;

export const fetchFromBusinessCentral = async (endpoint, filter, isApiV2 = false) => {
  try {
    const env = await getCurrentEnvironment();
    const encodedCompany = encodeBcCompanyName(env.company);
    // El id sólo se resuelve en la rama que lo necesita: OData va por nombre, y
    // pedirlo siempre obligaría a una llamada de más en cada lectura.
    const base = isApiV2
      ? `${env.bcBaseUrl}/${env.env}/api/excelia/automate/v1.0/companies(${await resolverCompanyId(env)})`
      : `${env.bcBaseUrl}/${env.env}/ODataV4/Company('${encodedCompany}')`;
    const url = `${base}/${endpoint}${filter ? `?$filter=${filter}` : ""}`;
    const response = await bcApi.get(url);
    const value = response.data.value;

    if (checkValueForErrors(value)) return "";

    // Cuando el resultado supera el tope de página, BC entrega la primera tanda
    // y un @odata.nextLink con el resto. Antes ese enlace se ignoraba: faltaban
    // filas sin error ni rastro en consola. Se resuelve acá y no en cada
    // llamador, así que los 16 servicios que usan este helper quedan cubiertos
    // sin cambiar ninguno.
    //
    // Hoy ninguna entidad de Ecuador lo devuelve (la mayor, direcciones, está en
    // 19.950 filas), así que esto es prevención -- pero el margen es estrecho.
    //
    // fetchPagedFromBusinessCentral no se ve afectado: pide bloques de 5.000 con
    // $top, muy por debajo del tope, así que sus respuestas nunca traen enlace.
    let rows = Array.isArray(value) ? value : [];
    let nextLink = response.data["@odata.nextLink"];
    let saltos = 0;

    while (nextLink && saltos < MAX_NEXT_LINKS) {
      const siguiente = await bcApi.get(nextLink);
      const masFilas = siguiente.data.value;

      if (Array.isArray(masFilas)) rows = rows.concat(masFilas);

      nextLink = siguiente.data["@odata.nextLink"];
      saltos += 1;
    }

    if (nextLink) {
      anotarAvisoDeTransporte(
        endpoint,
        `se alcanzó el tope de ${MAX_NEXT_LINKS} tandas encadenadas y Business Central todavía devuelve más — PUEDE HABER FILAS SIN TRAER`
      );
    }

    if (saltos > 0) {
      anotarTransporte(
        endpoint,
        `${rows.length} filas · superó el tope de página del servidor, se siguió el enlace ${saltos} ${
          saltos === 1 ? "vez" : "veces"
        }`
      );
    }

    // Se conserva el valor original cuando no es un arreglo: algunos endpoints
    // devuelven un escalar y hay llamadores que cuentan con eso.
    return Array.isArray(value) ? rows : value || [];
  } catch (error) {
    console.error(`Error al obtener datos de Business Central (${endpoint}):`, error);
    throw nombrarErrorDeLectura(error, endpoint);
  }
};

/**
 * Trae una entidad de BC por bloques, en vez de una sola respuesta con todo.
 *
 * ListaClientes son 18.643 registros / 8,9 MB y direcciones 19.950 / 4,4 MB:
 * pedirlos de una sola vez obliga a tener toda la respuesta en memoria y a
 * parsearla entera antes de poder guardar nada, y si se corta la conexión a
 * mitad no queda nada. Por bloques el costo total es prácticamente el mismo
 * (medido: 1,8 s de una sola vez contra 2,1 s en bloques de 5.000), pero cada
 * bloque se guarda apenas llega.
 *
 * Hay dos formas de paginar, y la que se use cambia las garantías:
 *
 * - **Por clave** (`keyField`): cada bloque pide lo posterior al último valor
 *   traído, `Source_Counter gt <último>`. Es estable aunque los datos cambien
 *   entre bloques, y le ahorra a BC recorrer y descartar las filas anteriores.
 *   Exige que la clave sea **única y creciente**: si dos filas la comparten, el
 *   `gt` se salta la segunda.
 * - **Por desplazamiento** (`$skip`, el respaldo): pide "los siguientes N desde
 *   la posición M". Si entre dos bloques se inserta o borra una fila, todo lo
 *   que sigue se corre y se salta o se repite un registro. Y el costo crece con
 *   la posición, porque el servidor recorre y descarta lo anterior.
 *
 * Se usa la clave donde se puede. Antes no se podía: `Source_Counter` estaba
 * repetido en Colegios -- 4.117 registros con 319 valores distintos y 1.663 en
 * cero-- y con valores repetidos la paginación por clave pierde filas. Business
 * Central lo corrigió en el origen y renumeró lo existente; medido contra el
 * ambiente el 21/08/2026: clientes 18.643/18.643 valores distintos, colegios
 * 4.117/4.117, direcciones 19.950/19.950, ninguno en cero.
 *
 * @param {string} endpoint - Entidad OData (ej. "ListaClientes").
 * @param {object} [options]
 * @param {string} [options.filter] - Filtro OData, sin el "$filter=".
 * @param {string} [options.keyField] - Campo por el que paginar. Debe ser único
 *   y creciente (ej. "Source_Counter"). Si se indica, manda sobre `orderBy`.
 * @param {string} [options.orderBy] - Campo de orden para el respaldo por
 *   desplazamiento. Debe ser único y estable.
 * @param {number} [options.pageSize=5000] - Tamaño de bloque.
 * @param {(rows: Array, procesados: number) => Promise<void>} [options.onPage]
 *   - Se llama con cada bloque apenas llega. Acá se guarda, para no acumular
 *   todo en memoria.
 * @param {(url: string) => Promise<Array>} [options.fetcher] - Quién trae cada
 *   bloque. Por defecto `fetchFromBusinessCentral`; se puede sustituir para
 *   probar el avance del cursor sin salir a la red, que es donde viviría un
 *   fallo de paginación.
 * @returns {Promise<number>} - Total de registros traídos.
 */
export const fetchPagedFromBusinessCentral = async (
  endpoint,
  {
    filter = "",
    keyField,
    orderBy,
    pageSize = 5000,
    onPage,
    fetcher = fetchFromBusinessCentral,
  } = {}
) => {
  let skip = 0;
  let cursor = null;
  let procesados = 0;

  for (;;) {
    const rows = await fetcher(
      `${endpoint}?${construirConsultaDePagina({ filter, keyField, orderBy, pageSize, skip, cursor })}`
    );
    const page = Array.isArray(rows) ? rows : [];

    if (page.length > 0) {
      procesados += page.length;
      if (onPage) await onPage(page, procesados);
    }

    // Un bloque incompleto significa que ya no hay más.
    if (page.length < pageSize) break;

    if (keyField) {
      const siguiente = mayorValorDe(page, keyField);

      // Sin avance no hay bloque siguiente que pedir: o la clave no viene en la
      // respuesta, o está repetida. Se corta en vez de pedir eternamente el
      // mismo bloque.
      if (siguiente === null || (cursor !== null && siguiente <= cursor)) {
        console.warn(
          `Paginación por ${keyField} detenida en ${procesados} registros: el valor no avanzó ` +
            `(último: ${siguiente}). Puede haber claves repetidas en ${endpoint}.`
        );
        break;
      }

      cursor = siguiente;
    } else {
      skip += pageSize;
    }
  }

  return procesados;
};

/**
 * Mayor valor numérico de un campo en un bloque.
 *
 * Se toma el máximo y no el último elemento a propósito: aunque se pida
 * `$orderby` por ese campo, el máximo no depende de que la respuesta venga
 * efectivamente ordenada.
 */
export const mayorValorDe = (rows, field) => {
  let mayor = null;

  for (const row of rows) {
    const valor = Number(row?.[field]);
    if (!Number.isFinite(valor)) continue;
    if (mayor === null || valor > mayor) mayor = valor;
  }

  return mayor;
};

/**
 * Arma los parámetros de consulta de un bloque. Aparte de
 * `fetchPagedFromBusinessCentral` para poder probarla: es donde vive la
 * diferencia entre paginar por clave y por desplazamiento.
 */
export const construirConsultaDePagina = ({
  filter = "",
  keyField,
  orderBy,
  pageSize,
  skip = 0,
  cursor = null,
}) => {
  const filtros = [];

  // El filtro del llamador y el del cursor se combinan: el primero acota qué se
  // busca, el segundo por dónde va la lectura.
  if (filter) filtros.push(`(${filter})`);
  if (keyField && cursor !== null) filtros.push(`${keyField} gt ${cursor}`);

  const params = [];
  if (filtros.length) params.push(`$filter=${filtros.join(" and ")}`);

  const orden = keyField || orderBy;
  if (orden) params.push(`$orderby=${orden}`);

  params.push(`$top=${pageSize}`);

  // El desplazamiento sólo se manda cuando NO se pagina por clave: mezclar los
  // dos volvería a saltarse filas.
  if (!keyField) params.push(`$skip=${skip}`);

  return params.join("&");
};

export const fetchFromBusinessCentralEspecial = async (endpoint, data = {}) => {
  try {
    const env = await getCurrentEnvironment();
    const encodedCompany = encodeBcCompanyName(env.company);
    const url = `${env.bcBaseUrl}/${env.env}/ODataV4/${endpoint}?company='${encodedCompany}'`;

    const response = await bcApi.post(url, data);
    const value = response.data.value;

    if (checkValueForErrors(value)) return "";
    return value || [];
  } catch (error) {
    console.error(`Error al obtener datos de Business Central (${endpoint}):`, error);
    throw nombrarErrorDeLectura(error, endpoint);
  }
};

export const sendToBusinessCentral = async (endpoint, data, isCustomEndpoint = false) => {
  try {
    const env = await getCurrentEnvironment();
    const encodedCompany = encodeBcCompanyName(env.company);
    const BC_BASE_URL = `${env.bcBaseUrl}/${env.env}/ODataV4`;

    const url = isCustomEndpoint
      ? `${BC_BASE_URL}/${endpoint}?company='${encodedCompany}'`
      : `${BC_BASE_URL}/Company('${encodedCompany}')/${endpoint}`;

    const response = await bcApi.post(url, data);
    const responseData = response.data;

    if (responseData.value && checkValueForErrors(responseData.value)) {
      // Se vacía `value` para que ningún llamador lo confunda con un resultado
      // válido, pero el motivo se conserva en `error` para poder mostrarlo.
      return { ...responseData, value: "", error: getBcErrorMessage(responseData.value) };
    }

    return responseData;
  } catch (error) {
    console.error(`Error al enviar datos a Business Central (${endpoint}):`, error);
    throw error;
  }
};

export const fetchInvoiceHistory = async (filter) => {
  try {
    const env = await getCurrentEnvironment();
    const encodedCompany = encodeBcCompanyName(env.company);

    const url = `${env.bcBaseUrl}/${env.env}/ODataV4/Company('${encodedCompany}')/salesInvoice?${filter}`;

    const response = await bcApi.get(url);
    return response.data?.value || [];
  } catch (error) {
    console.error(`Error al obtener facturas históricas:`, error);
    throw new Error(
      "No se pudieron obtener las facturas históricas. Inténtelo nuevamente más tarde."
    );
  }
};

/**
 * Registra un documento en Business Central y dice qué pasó, sin enseñar nada.
 *
 * Es el cuerpo que tenía `postInvoice`, separado de su modal. La sincronización
 * necesita el **motivo** del rechazo para decidir qué hace --un `PAGO_INCOMPLETO`
 * no se trata igual que cualquier otro--, y `postInvoice` sólo devolvía un
 * booleano después de enseñarlo. Acuerdo
 * `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H1.
 *
 * ## Por qué un abono de Costa Rica se registra sin enviar
 *
 * `postAndSend` registra y **después** manda el documento por el correo nativo de
 * Business Central. En Costa Rica ese envío no hace falta --el comprobante lo
 * envía CertificaE, que no usa el correo de BC-- y donde no hay cuenta de correo
 * falla con 400 **con el abono ya registrado y timbrado**: medido en Costa Rica
 * DEV el 13/09/2026, `VNC15-000332` → `VNR15-000313`, con clave, y la respuesta
 * «An email account must be configured to send emails». Acuerdo
 * `contrato-hallazgos-ciclo-cri-2026-09-12.md`.
 *
 * Paraguay y Honduras siguen con `postAndSend`, como hasta ahora.
 *
 * @param {string} systemId
 * @param {boolean} isCreditMemo
 * @param {object} [country] El país activo.
 * @returns {Promise<{ok: boolean, message: string}>}
 */
export const registrarEnBusinessCentral = async (systemId, isCreditMemo, country = null) => {
  try {
    const env = await getCurrentEnvironment();

    const baseUrl = `${env.bcBaseUrl}/${env.env}/api/v2.0/companies(${await resolverCompanyId(env)})`;
    const postInvoiceUrl = baseUrl + `/salesInvoices(${systemId})/Microsoft.NAV.post`;
    const accionDelAbono = comportamientoDelPais(country?.code, "accionDelAbono");
    const postCreditMemoUrl = baseUrl + `/salesCreditMemos(${systemId})/${accionDelAbono}`;

    const url = isCreditMemo ? postCreditMemoUrl : postInvoiceUrl;

    const response = await bcApi.post(url);
    if (response.status === 204) return { ok: true, message: "" };

    return {
      ok: false,
      message:
        response.data?.error?.message ||
        response.data?.error?.code ||
        `No se pudo contabilizar la factura. ${response?.message}`,
      origen: ORIGEN_BC,
    };
  } catch (error) {
    return {
      ok: false,
      message: formatAxiosError(error, `No se pudo contabilizar la factura. ${error?.message}`),
      // De dónde viene el fallo. `message` se deja igual porque hay reglas que
      // lo leen --el infrapago, el abono que excede-- y porque cuando contesta
      // Business Central es el texto bueno; lo que cambia es que ahora se sabe
      // cuándo **no** contestó nadie, y entonces no se le enseña en crudo.
      origen: origenDelFallo(error),
    };
  }
};

//Metodo para contabilizar una factura en el momento de sincronizar ( Paraguay y Honduras)
export const postInvoice = async (systemId, isCreditMemo, country = null) => {
  const { ok, message, origen } = await registrarEnBusinessCentral(systemId, isCreditMemo, country);
  if (ok) return true;

  const aviso = avisoDelFallo({
    origen,
    mensaje: message,
    queNoSePudo: "No se pudo contabilizar el documento en Business Central.",
  });
  window.messageApi.error(aviso.texto, { modal: true, title: aviso.titulo });
  return false;
};

// POST a la API REST de Punto de Venta (api/Excelia/Pos/v1.0/companies(id)/entidad)
export const postToBusinessCentralPos = async (endpoint, body) => {
  try {
    const env = await getCurrentEnvironment();
    const base = `${env.bcBaseUrl}/${env.env}/api/Excelia/Pos/v1.0/companies(${await resolverCompanyId(env)})`;
    const url = `${base}/${endpoint}`;
    const response = await bcApi.post(url, body);
    return response.data;
  } catch (error) {
    console.error(`Error al enviar datos a Business Central POS (${endpoint}):`, error);
    throw error;
  }
};

// GET a la API REST de Punto de Venta (api/Excelia/Pos/v1.0/companies(id)/entidad).
// `expand` es el valor crudo de $expand (ej. "ShiftsClosingLines($expand=ShiftsClosingLinesDetails)").
export const fetchFromBusinessCentralPos = async (endpoint, filter, expand) => {
  try {
    const env = await getCurrentEnvironment();
    const base = `${env.bcBaseUrl}/${env.env}/api/Excelia/Pos/v1.0/companies(${await resolverCompanyId(env)})`;
    const params = [];
    if (filter) params.push(`$filter=${filter}`);
    if (expand) params.push(`$expand=${expand}`);
    const url = `${base}/${endpoint}${params.length ? `?${params.join("&")}` : ""}`;
    const response = await bcApi.get(url);
    const value = response.data.value;

    if (checkValueForErrors(value)) return [];
    return value || [];
  } catch (error) {
    console.error(`Error al obtener datos de Business Central POS (${endpoint}):`, error);
    throw nombrarErrorDeLectura(error, endpoint);
  }
};

export const fetchFromApiV2 = async (endpoint, filter) => {
  try {
    const env = await getCurrentEnvironment();
    const base = `${env.bcBaseUrl}/${env.env}/api/v2.0/companies(${await resolverCompanyId(env)})`;
    const url = `${base}/${endpoint}${filter ? `?$filter=${filter}` : ""}`;
    const response = await bcApi.get(url);
    const value = response.data.value;

    if (checkValueForErrors(value)) return "";
    return value || [];
  } catch (error) {
    console.error(`Error al obtener datos de Business Central (${endpoint}):`, error);
    throw nombrarErrorDeLectura(error, endpoint);
  }
};

export const downloadInvoicePdfFromBC = async ({
  documentNo,
  documentType = "Invoice",
  fileName = "Factura.pdf",
}) => {
  try {
    const env = await getCurrentEnvironment();
    if (!documentNo) {
      window.messageApi.error("No se encontró número de documento para descargar el PDF.");
      return false;
    }

    const isCreditMemo = documentType === "Credit Memo";
    const endpoint = isCreditMemo ? "PDFMgt_GetCrMemoPdfBase64" : "PDFMgt_GetInvoicePdfBase64";
    const companyByName = `company='${encodeBcCompanyName(env.company)}'`;
    const url = `${env.bcBaseUrl}/${env.env}/ODataV4/${endpoint}?${companyByName}`;

    const response = await bcApi.post(url, { input: documentNo });
    const rawValue = response?.data?.value;
    const pdfBase64 =
      typeof rawValue === "string"
        ? rawValue.replace("data:application/pdf;base64,", "").trim()
        : "";

    if (!pdfBase64 || hasError(pdfBase64)) {
      throw new Error("Business Central no devolvió un PDF válido en base64.");
    }

    const binaryString = atob(pdfBase64);
    const byteNumbers = new Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      byteNumbers[i] = binaryString.codePointAt(i) || 0;
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return true;
  } catch (error) {
    const message = formatAxiosError(error, "No se pudo descargar el PDF de la factura.");

    window.messageApi.error(message, {
      modal: true,
      title: "Error Business Central",
    });

    return false;
  }
};
